import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import { providerApi } from '../../../services/api';
import OrderChatDialog from '../OrderChatDialog';
import { ShipOrderDialog, DeliverOrderDialog } from '../StoreFulfilmentDialogs';
import { hasFeature, FEATURES } from '../../../plan/plan';
import useProviderOrders from './useProviderOrders';
import OrdersTable from './OrdersTable';
import OrderDetailsDialog from './OrderDetailsDialog';
import OrdersToolbar from './OrdersToolbar';
import OrdersCalendar, { MODES } from './calendar/OrdersCalendar';
import { rangeFor } from './calendar/calendarRange';
import { DEFAULT_FILTERS, applyFilters, isClosed, isUndated, toDateStr } from './orderFilters';

const VIEW_KEY = 'anipaw.providerOrders.view';
const readStoredView = () => { try { return localStorage.getItem(VIEW_KEY); } catch { return null; } };
const storeView = (v) => { try { localStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ } };
// providerData.availabilityDates arrives as ISO strings or date arrays depending on the serializer.
const normaliseDates = (dates) => (dates || []).map(toDateStr).filter(Boolean);

/**
 * Props: providerData, onUpgradeClick, onProviderRefresh (re-reads providerData after the calendar's
 * availability shortcut writes; Dashboard passes the auth context's refreshProviderData. A prop rather
 * than useAuth() here, so this page has no Firebase import and stays unit-testable).
 */
export default function OrdersPage({ providerData, onUpgradeClick, onProviderRefresh }) {
  const { t, i18n } = useTranslation();
  const o = useProviderOrders(providerData);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const calendarAvailable = providerData.providerType === 'SERVICE';
  // Free SERVICE providers see the toggle locked (upsell); the page itself is open to them.
  const calendarLocked = calendarAvailable && !hasFeature(providerData, FEATURES.CALENDAR);
  const visibleOrders = useMemo(() => applyFilters(o.orders, filters, o.notesSummary), [o.orders, filters, o.notesSummary]);

  // ---- calendar position: URL wins over the stored choice, which wins over the default ----------
  const locale = i18n.language === 'he' ? he : enUS;
  const theme = useTheme(); // the default theme outside a ThemeProvider (tests)
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = searchParams.get('view');
  const urlMode = searchParams.get('cal');
  const urlDate = searchParams.get('date');

  const [view, setViewState] = useState(() => {
    if (!calendarAvailable || calendarLocked) return 'list';
    if (urlView === 'calendar' || urlView === 'list') return urlView;
    return readStoredView() || 'calendar';
  });
  const [calMode, setCalMode] = useState(MODES.includes(urlMode) ? urlMode : (isSmall ? 'agenda' : 'month'));
  const [calDate, setCalDate] = useState(() => {
    const parsed = urlDate ? parseISO(urlDate) : null;
    return parsed && isValid(parsed) ? parsed : new Date();
  });
  const [availabilityDates, setAvailabilityDates] = useState(() => normaliseDates(providerData.availabilityDates));
  useEffect(() => { setAvailabilityDates(normaliseDates(providerData.availabilityDates)); }, [providerData.availabilityDates]);

  const setView = (v) => { setViewState(v); storeView(v); };

  // Mirror the calendar position into the URL so a link reopens the same place.
  useEffect(() => {
    if (!calendarAvailable) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', view);
      if (view === 'calendar') {
        next.set('cal', calMode);
        next.set('date', format(calDate, 'yyyy-MM-dd'));
      } else {
        next.delete('cal');
        next.delete('date');
      }
      return next;
    }, { replace: true });
  }, [view, calMode, calDate, calendarAvailable, setSearchParams]);

  // Fetch the visible range from the server whenever the calendar position changes (design §8).
  const { loadCalendarRange } = o;
  useEffect(() => {
    if (view !== 'calendar') return;
    const { from, to } = rangeFor(calMode, calDate, locale);
    loadCalendarRange(from, to);
  }, [view, calMode, calDate, locale, loadCalendarRange]);

  const calendarOrders = useMemo(
    () => applyFilters(o.calendarOrders || [], filters, o.notesSummary),
    [o.calendarOrders, filters, o.notesSummary],
  );
  // Open inquiries with no booked start never come back from the range endpoint; count them from the list.
  const undatedCount = useMemo(() => o.orders.filter((order) => !isClosed(order) && isUndated(order)).length, [o.orders]);

  const canEditAvailability = hasFeature(providerData, FEATURES.AVAILABILITY);
  const toggleAvailability = useCallback(async (dayKey, nowAvailable) => {
    if (!canEditAvailability) { onUpgradeClick?.(); return; }
    const next = nowAvailable ? availabilityDates.filter((d) => d !== dayKey) : [...availabilityDates, dayKey].sort();
    try {
      await providerApi.updateAvailability({ availabilityDates: next, availabilityRadius: providerData.availabilityRadius ?? null });
      setAvailabilityDates(next);
      o.setSuccess(t('orders.calendar.availabilityUpdated'));
      await onProviderRefresh?.();
    } catch (err) {
      o.setError(err.response?.data?.message || t('orders.calendar.availabilityFailed'));
    }
  }, [availabilityDates, providerData.availabilityRadius, onProviderRefresh, o, t, canEditAvailability, onUpgradeClick]);

  if (o.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t('orders.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('orders.subtitle')}
        </Typography>

        {providerData.providerType === 'SERVICE' && !providerData.isProPlan && (
          <Alert
            severity="info"
            data-testid="orders-upgrade-hint"
            sx={{ mb: 2 }}
            onClick={() => window.dispatchEvent(new Event('anipaw:pro-required'))}
            action={null}
          >
            {t('orders.upgradeHint')}
          </Alert>
        )}

        {o.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {o.error}
          </Alert>
        )}

        {o.success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {o.success}
          </Alert>
        )}

        {/* The calendar stays reachable when the newest-500 list is empty but older bookings exist. */}
        {o.orders.length === 0 && view !== 'calendar' ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t('orders.empty')}
            </Typography>
          </Box>
        ) : (
          <>
            <OrdersToolbar
              filters={filters}
              onFiltersChange={setFilters}
              view={view}
              onViewChange={setView}
              calendarAvailable={calendarAvailable}
              calendarLocked={calendarLocked}
              onUpgradeClick={onUpgradeClick}
            />
            {view === 'calendar' ? (
              <OrdersCalendar
                orders={calendarOrders}
                loading={o.calendarOrders === null || o.calendarLoading}
                notesSummary={o.notesSummary}
                availabilityDates={availabilityDates}
                mode={calMode}
                onModeChange={setCalMode}
                date={calDate}
                onDateChange={setCalDate}
                onOpenOrder={(order) => o.openDetails(order)}
                onToggleAvailability={toggleAvailability}
                canEditAvailability={canEditAvailability}
                undatedCount={undatedCount}
                onShowUndated={() => { setView('list'); setFilters({ ...DEFAULT_FILTERS, scope: 'open', time: 'undated' }); }}
              />
            ) : visibleOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {t('orders.filters.noMatch')}
                </Typography>
              </Box>
            ) : (
              <OrdersTable
                orders={visibleOrders}
                txByOrder={o.txByOrder}
                notesSummary={o.notesSummary}
                actionLoading={o.actionLoading}
                onViewDetails={(order) => o.openDetails(order)}
                onQuickNote={(order) => o.openDetails(order, 'notes')}
                onChat={o.setChatOrder}
                onComplete={(id) => o.handleOrderAction(id, 'complete')}
                onShip={o.setShipOrder}
                onDeliver={o.setDeliverOrder}
                onCancel={o.requestCancel}
              />
            )}
          </>
        )}

        <OrderDetailsDialog
          open={o.showOrderDetails}
          order={o.selectedOrder}
          txByOrder={o.txByOrder}
          actionLoading={o.actionLoading}
          initialTab={o.detailsTab}
          onClose={o.closeDetails}
          onComplete={(id) => o.handleOrderAction(id, 'complete')}
          onShip={o.setShipOrder}
          onDeliver={o.setDeliverOrder}
          onCancel={o.requestCancel}
          onMarkPaid={o.handleMarkPaid}
          onNotesChanged={o.refreshNotesSummary}
        />

        <Dialog
          open={Boolean(o.confirmCancelOrder)}
          onClose={() => o.setConfirmCancelOrder(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t('payouts.cancelAfterPayoutTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{t('payouts.cancelAfterPayoutBody')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => o.setConfirmCancelOrder(null)}>{t('payouts.cancelAfterPayoutKeep')}</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                const target = o.confirmCancelOrder;
                o.setConfirmCancelOrder(null);
                o.handleOrderAction(target.id, 'cancel');
              }}
            >
              {t('payouts.cancelAfterPayoutConfirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <ShipOrderDialog
          open={Boolean(o.shipOrder)}
          order={o.shipOrder}
          onClose={() => o.setShipOrder(null)}
          onDone={() => o.handleFulfilmentDone(t('fulfilment.shippedOk'))}
        />
        <DeliverOrderDialog
          open={Boolean(o.deliverOrder)}
          order={o.deliverOrder}
          onClose={() => o.setDeliverOrder(null)}
          onDone={() => o.handleFulfilmentDone(t('fulfilment.deliveredOk'))}
        />

        {/* Chat + quote + actions dialog */}
        {o.chatOrder && (
          <OrderChatDialog
            open={Boolean(o.chatOrder)}
            order={o.chatOrder}
            providerData={providerData}
            onClose={() => o.setChatOrder(null)}
            onUpdated={() => o.refresh()}
            onUpgradeClick={onUpgradeClick}
          />
        )}
      </CardContent>
    </Card>
  );
}
